import { isoDateTime, z } from "../zod.js";
import { Uuid } from "../identity/admin-schemas.js";
import {
  OpportunityStatus,
  SalesBusinessDate,
  SalesCurrency,
  SalesLocaleName,
  SalesMinorMoneyNonNegative,
  SalesNextActivity,
} from "./schemas.js";
import { QuoteRejectionReason, QuoteStatus } from "./quote-schemas.js";

// Module 09 — the PIPELINE's contract DTOs (spec 09 §2.24, §2.25, §3.11, §4.14
// and §5's P3-E2 PR E2-1 rows; D-312). A deal (`opportunities`, "Сделка" on
// the glass, D-312 ruling 19) belongs to one customer, sits in one of the
// tenant's pack-seeded stages (`pipeline_stages`), names the month its owner
// expects it to close, and carries an expected value that may be left empty.
// Routes live in `./opportunity-register.ts`.
//
// WHAT THIS FILE HOLDS, AND THE RULES IT RESTATES BECAUSE THEY BITE HERE:
//  * REDACTION IS OMISSION (spec 09 §7): `expected_value_minor`,
//    `weighted_value_minor`, a linked quote's `total_minor` and the board's
//    `values` are `.optional()` because `sales.read_value` gates them. The
//    first two are ALSO `.nullable()`, a different fact: absent = you may not
//    see it, null = it does not exist (nobody estimated the value, or the
//    deal is not open and so has no weighted value). The forecast is a VALUE
//    DOOR (§7 narrowing 7), refused 403 without the grant, so its sums are
//    required.
//  * DERIVED FIELDS ARE THE SERVER'S (§2.25 "Nothing derived is stored"):
//    `idle_days`, `rotting`, `weighted_value_minor`, `close_month_passed`, the
//    next step and `needs_next_step` are computed when the deal is read, so
//    every reader agrees on red. The weighted value is always the value of
//    the SQL function `sales_weighted_minor(value, pct)`, which rounds
//    half-even once per deal, so the list adds up to the board's and the
//    forecast's cells exactly (D-312 ruling 10).
//  * A LABEL FOLLOWS ITS OWN DOOR (D-312 ruling 21): `customer_name`, each
//    `won_quote_*` / `won_order_*` pair and `won_order_cancelled` are null to
//    a reader whose door for that record refuses them, and `linked_quotes` /
//    `linked_quote_count` hold only the quotes the reader's quote door
//    answers. `won_quote_id` and `won_order_id` travel with their labels and
//    are null with them. The deal's own ids (`customer_id`, `owner_user_id`,
//    `stage_id`, `source_conversation_id`) are served whatever the reader.
//  * MONTHS are `YYYY-MM` text (`SalesMonth`); the column is a `date` pinned
//    to the first of its month (§2.25's CHECK).
//  * D-225: no server-minted prose. Every refusal is a code in
//    `salesProblemCodes` (./schemas.ts), which stays the ONE sales catalog.

// --- shared scalars ---------------------------------------------------------

/** A calendar month `YYYY-MM` — a deal's expected close month, a forecast
 * bound, a list's `close_from` / `close_to`. The month is 01 to 12 and the
 * year 1900 to 2099: a year a `date` column cannot hold (Postgres has no year
 * 0) answered 500 at four doors and in a shared saved view, and the
 * forecast's defaulted end (`from_month` + 5) must stay a four-digit year. */
export const SalesMonth = z
  .string()
  .regex(/^(19|20)\d{2}-(0[1-9]|1[0-2])$/u)
  .openapi({
    example: "2026-10",
    description: "A calendar month, `YYYY-MM`, from 1900-01 to 2099-12",
  });

/** How a won deal was won (§2.25 `won_via` CHECK): a person's win, or the
 * customer's acceptance of a linked quote (§3.5 step 7a, the system actor). */
export const OpportunityWonVia = z.enum(["manual", "quote_accepted"]).openapi("OpportunityWonVia");

/** The lost-reason vocabulary is §2.12's own four codes, so a deal's loss and
 * a quote's rejection read one vocabulary (D-312 ruling 9). */
export const OpportunityLostReason = QuoteRejectionReason;

/** The widest board column and the most linked quotes a detail carries. */
export const OPPORTUNITY_BOARD_COLUMN_LIMIT = 25;
export const OPPORTUNITY_LINKED_QUOTES_LIMIT = 20;
/** The widest forecast range: `to_month` is at most this many months after
 * `from_month` (spec 09 §5: "at most 24 months apart"). */
export const OPPORTUNITY_FORECAST_MAX_SPAN_MONTHS = 24;
/** A lost note's cap and a title's cap (§2.25). */
export const OPPORTUNITY_TITLE_MAX = 200;
export const OPPORTUNITY_LOST_NOTE_MAX = 2000;

// --- pipeline stages (§2.24) ------------------------------------------------

/**
 * One of the tenant's stages (§2.24). Pack data, adopted by `code` and never
 * rewritten by an install; a tenant tunes only `probability_pct` and
 * `rotting_after_days` (D-312 ruling 2). Stage names come from `name_i18n` in
 * the reader's language, never from the dictionaries; `name` is the row-local
 * English projection (the D-209 `name` / `name_i18n` pair, as §2.10's classes
 * carry it).
 */
export const PipelineStage = z
  .object({
    id: Uuid,
    code: z.string().regex(/^[A-Z][A-Z0-9_]{1,47}$/u),
    name_i18n: SalesLocaleName,
    name: z.string(),
    /** 1 to 50, unique per tenant; the board's column order. */
    position: z.number().int().min(1).max(50),
    /** 0 to 100, the forecast's weight (§3.11). */
    probability_pct: z.number().int().min(0).max(100),
    /** 1 to 365, or null: a deal never rots in this stage. */
    rotting_after_days: z.number().int().min(1).max(365).nullable(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("PipelineStage");

/** `GET /v1/pipeline-stages` — the tenant's stages by position, at most 50,
 * unpaginated (a bounded definition set). Empty for a tenant whose installed
 * pack predates the kind (D-312 ruling 3). */
export const PipelineStageListResponse = z
  .object({ items: z.array(PipelineStage).max(50) })
  .openapi("PipelineStageListResponse");

/**
 * `PATCH /v1/pipeline-stages/{id}` (§4.13's Pipeline stages card, D-312
 * ruling 2) — the two numbers a tenant tunes, at least one of them.
 * `rotting_after_days: null` clears the rotting clock for that stage;
 * `probability_pct` is never null. Nothing else about a stage changes.
 */
export const PipelineStageUpdateRequest = z
  .object({
    probability_pct: z.number().int().min(0).max(100).optional(),
    rotting_after_days: z.number().int().min(1).max(365).nullable().optional(),
  })
  .strict()
  .refine(
    (patch) => patch.probability_pct !== undefined || patch.rotting_after_days !== undefined,
    {
      message: "at least one of probability_pct or rotting_after_days is required",
      params: { reason: "empty_patch" },
    },
  )
  .openapi("PipelineStageUpdateRequest");

// --- the deal (§2.25) --------------------------------------------------------

/**
 * One deal as the board, the list and the deal page read it (spec 09 §5's
 * "The items" paragraph, verbatim in shape). See this file's header for the
 * omission / null rules and the two-doors rule.
 */
export const Opportunity = z
  .object({
    id: Uuid,
    /** `OPP-{YYYY}-{5+ digits}` from the tenant's `OPP` number series. */
    opportunity_no: z.string().regex(/^OPP-\d{4}-\d{5,}$/u),
    /** The person's own words, 1 to 200 characters. */
    title: z.string(),
    customer_id: Uuid,
    /** The customer's display name. NULL to a reader the customer door
     * refuses (a sales principal who owns the deal on a customer no longer
     * assigned to them, D-312 ruling 21). */
    customer_name: z.string().nullable(),
    owner_user_id: Uuid,
    /** NULL when the owner's display name cannot be resolved. */
    owner_name: z.string().nullable(),
    stage_id: Uuid,
    stage_code: z.string(),
    /** The stage's CURRENT probability (live, never frozen on the deal). */
    probability_pct: z.number().int().min(0).max(100),
    /** The rotting clock: written at create, on every move and on reopen. */
    stage_entered_at: isoDateTime(),
    /** Whole 24-hour days since `stage_entered_at` (§3.11). */
    idle_days: z.number().int().min(0),
    /** The stage names `rotting_after_days` and more than that many days have
     * passed. Always false on a closed deal. */
    rotting: z.boolean(),
    status: OpportunityStatus,
    /** The customer's `default_currency` at create, frozen (§9.13). */
    currency: SalesCurrency,
    /** §7 OMISSION — `sales.read_value`. NULL: nobody estimated the value,
     * which no reader counts as zero. The total the customer would pay, VAT
     * included (D-312 ruling 7). */
    expected_value_minor: SalesMinorMoneyNonNegative.nullable().optional(),
    /** §7 OMISSION — `sales.read_value`. `sales_weighted_minor(value, pct)`
     * for an OPEN deal with a value; NULL for an unvalued deal and for a deal
     * that is not open. */
    weighted_value_minor: SalesMinorMoneyNonNegative.nullable().optional(),
    expected_close_month: SalesMonth,
    /** The tenant's current month is past `expected_close_month` (the
     * forecast's slipped row, red on the glass). */
    close_month_passed: z.boolean(),
    /** The earliest open task on the deal, by kind and date only, served to
     * every reader of the deal (D-313 ruling 6). */
    next_activity: SalesNextActivity.nullable(),
    /** An OPEN deal with no open task (D-313 ruling 7). Always false on a
     * closed deal. */
    needs_next_step: z.boolean(),
    /** The module 17 conversation the deal came from; no FK (§2.25). */
    source_conversation_id: Uuid.nullable(),
    /** The linked quotes THIS reader's quote door answers (D-312 ruling 21). */
    linked_quote_count: z.number().int().min(0),
    won_via: OpportunityWonVia.nullable(),
    /** The pairs are NULL when the deal was not won through that document,
     * and when this reader's quote or order door refuses it (D-312 ruling
     * 21). */
    won_quote_id: Uuid.nullable(),
    won_quote_no: z.string().nullable(),
    won_order_id: Uuid.nullable(),
    won_order_no: z.string().nullable(),
    /** True when the won order has been cancelled, false otherwise (a deal
     * with no won order included). NULL to a reader whose order door refuses
     * the won order. What the deal page's Reopen and the forecast's cancelled
     * count read (D-312 ruling 9). */
    won_order_cancelled: z.boolean().nullable(),
    lost_reason_code: OpportunityLostReason.nullable(),
    /** Stored as typed, at most 2000 characters. */
    lost_note: z.string().nullable(),
    /** Set exactly when the deal is not open. */
    closed_at: isoDateTime().nullable(),
    /** NULL when the customer's acceptance closed the deal. */
    closed_by: Uuid.nullable(),
    /** Whether THIS caller's write doors answer for the deal (the D-118
     * `facility_writable` posture): the caller holds `sales.opportunity.write`
     * and narrowing 16 admits them on this deal. It says nothing about the
     * deal's status, which each door checks itself. */
    mutable: z.boolean(),
    /** A user or an agent principal. */
    created_by: Uuid,
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("Opportunity");

/** One linked quote on the deal page (at most 20, newest first). Only the
 * quotes this reader's quote door answers (D-312 ruling 21). */
export const OpportunityLinkedQuote = z
  .object({
    id: Uuid,
    quote_no: z.string(),
    status: QuoteStatus,
    currency: SalesCurrency,
    /** §7 OMISSION — `sales.read_value`. The current revision's total. */
    total_minor: SalesMinorMoneyNonNegative.optional(),
    valid_until: SalesBusinessDate.nullable(),
    /** The §2.12 lost-reason code a rejected quote was recorded with, which
     * the Lost dialog pre-selects from the newest rejected linked quote.
     * NULL when the quote is not rejected or carried no code. */
    rejection_reason_code: QuoteRejectionReason.nullable(),
  })
  .openapi("OpportunityLinkedQuote");

/** `GET /v1/opportunities/{id}` — the deal page's one read. Spread rather than
 * `Opportunity.extend(...)`, so it publishes as one flat object and not as an
 * `allOf` that oasdiff compares branch by branch (the `CustomerWithNextStep`
 * reason in `./schemas.ts`). */
export const OpportunityDetail = z
  .object({
    ...Opportunity.shape,
    linked_quotes: z.array(OpportunityLinkedQuote).max(OPPORTUNITY_LINKED_QUOTES_LIMIT),
  })
  .openapi("OpportunityDetail");

// --- the list (§4.14) -----------------------------------------------------------

/**
 * The typed order of `GET /v1/opportunities` (§4.14; `-` prefix =
 * descending). `expected_close_month` is the DECLARED DEFAULT the server
 * applies when the parameter is absent, and it breaks ties by `created_at`
 * then `id`, which is the board's column order too, so a board column's
 * `next_cursor` continues on this list (§5's board row). The value tokens are
 * fenced: `expected_value_minor` and `-expected_value_minor` answer 403
 * `missing_permission: sales.read_value` to a caller without the grant,
 * before the cursor is decoded (§7 narrowing 12, D-301); NULL values sort
 * last in both directions. Every other token is a field every reader
 * receives.
 */
export const OpportunitiesOrder = z
  .enum([
    "expected_close_month",
    "-expected_close_month",
    "expected_value_minor",
    "-expected_value_minor",
    "stage_entered_at",
    "-stage_entered_at",
    "-updated_at",
  ])
  .openapi("OpportunitiesOrder");

/** A filter that has one meaningful value. `rotting=false` is not "the deals
 * that do not rot" and is refused 400 rather than read as no filter, the
 * `shippable=true` shape of `GET /v1/orders`. */
const TrueOnlyQuery = z.enum(["true"]).optional();

/**
 * `GET /v1/opportunities` query (§5, §4.14). Every filter is optional and
 * they AND together; a value outside its enum, an id that is not a uuid or a
 * month that is not `YYYY-MM` is 400 VALIDATION_FAILED. The controller parses
 * with THIS schema AFTER its two 403s (the lineage, then a value `order` for a
 * caller without the grant), so the contract and the door agree on what a
 * filter admits.
 *  * `status` absent means `open`;
 *  * `close_from` / `close_to` bound `expected_close_month`, both inclusive;
 *  * `rotting` and `needs_next_step` take `true` only;
 *  * `lost_reason` matches a lost deal's `lost_reason_code`;
 *  * `q` matches the title, the number and the customer's display name,
 *    LIKE-escaped (#209), and the name only on deals whose customer the
 *    caller's customer door answers (D-312 ruling 21, the D-306 rule);
 *  * a `customer_id`, `owner_user_id` or `stage_id` the caller cannot read
 *    answers an empty page, never a 404.
 * No filter reads a value (spec 16 §9.23).
 */
export const OpportunityListQuery = z.object({
  status: OpportunityStatus.optional(),
  stage_id: z.guid().optional(),
  owner_user_id: z.guid().optional(),
  customer_id: z.guid().optional(),
  close_from: SalesMonth.optional(),
  close_to: SalesMonth.optional(),
  rotting: TrueOnlyQuery,
  needs_next_step: TrueOnlyQuery,
  lost_reason: OpportunityLostReason.optional(),
  q: z.string().max(200).optional(),
  order: OpportunitiesOrder.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

export const OpportunityListResponse = z
  .object({ items: z.array(Opportunity), next_cursor: z.string().nullable() })
  .openapi("OpportunityListResponse");

// --- the board (§4.14) ------------------------------------------------------------

/** `GET /v1/opportunities/board` query — the two filters the board shares
 * with the list (D-312 ruling 20: the board carries no saved views). */
export const OpportunityBoardQuery = z.object({
  owner_user_id: z.guid().optional(),
  customer_id: z.guid().optional(),
});

/** One currency's sums in a board column, over the WHOLE stage and never over
 * the cards the column has loaded. */
export const OpportunityBoardValues = z
  .object({
    currency: SalesCurrency,
    open_value_minor: SalesMinorMoneyNonNegative,
    /** The sum of the column's per-deal `sales_weighted_minor` values, each
     * rounded once, so the list filtered to the stage adds up to it. */
    weighted_value_minor: SalesMinorMoneyNonNegative,
    /** Open deals in this currency with no value, counted apart. */
    unvalued_count: z.number().int().min(0),
  })
  .openapi("OpportunityBoardValues");

export const OpportunityBoardColumn = z
  .object({
    stage: PipelineStage,
    /** Every open deal in the stage the reader's deal door answers. */
    open_count: z.number().int().min(0),
    rotting_count: z.number().int().min(0),
    /** §7 OMISSION — `sales.read_value`: the key is absent for a reader
     * without the grant, and one entry per currency otherwise (an empty
     * array for a stage with no open deal). */
    values: z.array(OpportunityBoardValues).optional(),
    /** At most 25 open deals in board order (`expected_close_month`, then
     * `created_at`, then `id`). */
    items: z.array(Opportunity).max(OPPORTUNITY_BOARD_COLUMN_LIMIT),
    /** A `GET /v1/opportunities` cursor for that route's DEFAULT order: Show
     * more calls `GET /v1/opportunities?status=open&stage_id=<stage>` with the
     * board's own filters, this cursor and `limit=25`, and the page continues
     * the column with no gap and no repeat. NULL when the column holds every
     * open deal of the stage. */
    next_cursor: z.string().nullable(),
  })
  .openapi("OpportunityBoardColumn");

/** `GET /v1/opportunities/board` — the board's one read: one column per stage
 * in position order. */
export const OpportunityBoardResponse = z
  .object({
    /** The tenant's current month, `YYYY-MM`, which `close_month_passed` is
     * judged against. */
    tenant_month: SalesMonth,
    stages: z.array(OpportunityBoardColumn).max(50),
  })
  .openapi("OpportunityBoardResponse");

// --- the forecast (§3.11) ---------------------------------------------------------

/** `GET /v1/opportunities/forecast` query. An absent bound takes its default
 * (the tenant's current month for `from_month`, five months after it for
 * `to_month`); the resolved pair must run forward and span at most
 * `OPPORTUNITY_FORECAST_MAX_SPAN_MONTHS` months, else 400 — checked after the
 * two 403s, like every 400 of this door. A `customer_id` the caller cannot
 * read answers empty rows. */
export const OpportunityForecastQuery = z.object({
  from_month: SalesMonth.optional(),
  to_month: SalesMonth.optional(),
  owner_user_id: z.guid().optional(),
  customer_id: z.guid().optional(),
});

/**
 * One close month in one currency (§3.11). OPEN deals count by their close
 * month; WON deals count in the tenant month they closed: at the won order's
 * current `total_minor` in the order's currency when an order won them, at
 * their expected value otherwise, and counted in `won_cancelled_count` with
 * no value when that order has been cancelled. For a reader whose order door
 * refuses the won order, the deal counts at its expected value in the deal's
 * currency (or in `won_unvalued_count`) and never in `won_cancelled_count`
 * (D-312 ruling 21). No cell adds two currencies.
 */
export const OpportunityForecastRow = z
  .object({
    close_month: SalesMonth,
    currency: SalesCurrency,
    open_count: z.number().int().min(0),
    /** Open deals with no expected value, counted apart and never as zero. */
    unvalued_count: z.number().int().min(0),
    open_value_minor: SalesMinorMoneyNonNegative,
    /** The sum of the per-deal `sales_weighted_minor` values, so the list
     * filtered to this month adds up to it exactly. */
    weighted_value_minor: SalesMinorMoneyNonNegative,
    won_count: z.number().int().min(0),
    won_unvalued_count: z.number().int().min(0),
    won_cancelled_count: z.number().int().min(0),
    won_value_minor: SalesMinorMoneyNonNegative,
  })
  .openapi("OpportunityForecastRow");

/** Open deals whose close month has already passed, per currency — their own
 * row, never folded into this month (§3.11). */
export const OpportunityForecastSlipped = z
  .object({
    currency: SalesCurrency,
    open_count: z.number().int().min(0),
    unvalued_count: z.number().int().min(0),
    open_value_minor: SalesMinorMoneyNonNegative,
    weighted_value_minor: SalesMinorMoneyNonNegative,
  })
  .openapi("OpportunityForecastSlipped");

export const OpportunityForecastResponse = z
  .object({
    tenant_month: SalesMonth,
    rows: z.array(OpportunityForecastRow),
    slipped: z.array(OpportunityForecastSlipped),
  })
  .openapi("OpportunityForecastResponse");

// --- the writes (§5) --------------------------------------------------------------

/** A deal's title: trimmed, 1 to 200 characters. */
const OpportunityTitle = z.string().trim().min(1).max(OPPORTUNITY_TITLE_MAX);

/**
 * `POST /v1/opportunities` (§5, §3.11). `stage_id` absent means the first
 * stage; `owner_user_id` absent means the caller for a sales principal and the
 * customer's §2.2 owner (else the caller) for an admin or an agent, and only
 * a literal admin names another owner. `expected_value_minor` absent or null
 * means nobody estimated it, and NAMING it (null included) from a caller
 * without `sales.read_value` is refused 403 before anything else (D-312
 * ruling 12). `quote_id` links an existing quote of the customer that is not
 * terminal and not linked elsewhere, locked before the deal's insert.
 * `source_conversation_id` is checked through module 17's port.
 */
export const OpportunityCreateRequest = z
  .object({
    customer_id: Uuid,
    title: OpportunityTitle,
    stage_id: Uuid.optional(),
    expected_value_minor: SalesMinorMoneyNonNegative.nullable().optional(),
    expected_close_month: SalesMonth,
    owner_user_id: Uuid.optional(),
    quote_id: Uuid.optional(),
    source_conversation_id: Uuid.optional(),
  })
  .strict()
  .openapi("OpportunityCreateRequest");

/**
 * `PATCH /v1/opportunities/{id}` — at least one key. `expected_value_minor:
 * null` clears the estimate. The value and the close month change only on an
 * open deal (409 `OPPORTUNITY_NOT_OPEN`), and the title and the owner stay
 * editable on a closed one. The stage never changes here; that is the move.
 */
export const OpportunityUpdateRequest = z
  .object({
    title: OpportunityTitle.optional(),
    expected_value_minor: SalesMinorMoneyNonNegative.nullable().optional(),
    expected_close_month: SalesMonth.optional(),
    owner_user_id: Uuid.optional(),
  })
  .strict()
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message:
      "at least one of title, expected_value_minor, expected_close_month or owner_user_id is required",
    params: { reason: "empty_patch" },
  })
  .openapi("OpportunityUpdateRequest");

/** `POST /v1/opportunities/{id}/move` — `from_stage_id` is the stage the card
 * left; the server compares it under the row lock (§3.11). A move to the
 * deal's own stage answers 200 and writes nothing. */
export const OpportunityMoveRequest = z
  .object({
    stage_id: Uuid,
    from_stage_id: Uuid,
  })
  .strict()
  .openapi("OpportunityMoveRequest");

/** `POST /v1/opportunities/{id}/win` — optionally naming the business that won
 * it: a CONVERTED quote of the customer (whose order is then stamped as
 * `won_order_id`) or a confirmed order of the customer, never both. */
export const OpportunityWinRequest = z
  .object({
    quote_id: Uuid.optional(),
    sales_order_id: Uuid.optional(),
  })
  .strict()
  .refine((body) => body.quote_id === undefined || body.sales_order_id === undefined, {
    message: "name a quote or an order, not both",
    path: ["sales_order_id"],
    params: { reason: "quote_and_order" },
  })
  .openapi("OpportunityWinRequest");

/** `POST /v1/opportunities/{id}/lose` — one of §2.12's four reasons and an
 * optional note stored as typed. Losing a deal leaves its quotes and tasks as
 * they are. */
export const OpportunityLoseRequest = z
  .object({
    reason_code: OpportunityLostReason,
    note: z.string().max(OPPORTUNITY_LOST_NOTE_MAX).nullish(),
  })
  .strict()
  .openapi("OpportunityLoseRequest");

/**
 * `POST /v1/quotes/{id}/opportunity` — link the quote to a deal, or unlink it
 * with `opportunity_id: null`. The key is required, so an empty body is a 400
 * rather than a silent unlink. Re-pointing a linked quote needs an unlink
 * first (409 `QUOTE_LINKED_ELSEWHERE`).
 */
export const QuoteOpportunityLinkRequest = z
  .object({
    opportunity_id: Uuid.nullable(),
  })
  .strict()
  .openapi("QuoteOpportunityLinkRequest");

export const QuoteOpportunityLinkResponse = z
  .object({
    quote_id: Uuid,
    opportunity_id: Uuid.nullable(),
  })
  .openapi("QuoteOpportunityLinkResponse");

/** The 409 body of the move: `OPPORTUNITY_STAGE_CHANGED` carries
 * `current_stage_id`, the stage the deal is in, so the board can name it and
 * put the card there (§4.14). Merged into the problem document beside `code`,
 * as every RFC 9457 extension is; absent on every other code. */
export const OpportunityMoveConflictExtensions = z
  .object({
    current_stage_id: Uuid,
  })
  .strict();

// --- the agent tool (spec 09 §8, D-312 ruling 18) ----------------------------------

/** `sales.get_customer_pipeline` params. Like every sales tool, no
 * `tenant_id`: the tool runs under the caller's tenant-bound context. */
export const GetCustomerPipelineParams = z.object({
  customer_id: Uuid,
});

/**
 * One open deal as `sales.get_customer_pipeline` returns it (§8): number,
 * title, stage code, close month, rotting, next step and value, filtered by
 * §7 narrowing 15 and the two-doors rule for the tool's own principal. The
 * value keys follow the service role's `sales.read_value` exactly as the HTTP
 * list does (omitted when withheld, null when unvalued), and the weighted
 * value is `sales_weighted_minor`'s.
 */
export const CustomerPipelineToolDeal = z.object({
  id: Uuid,
  opportunity_no: z.string(),
  title: z.string(),
  stage_code: z.string(),
  probability_pct: z.number().int().min(0).max(100),
  expected_close_month: SalesMonth,
  close_month_passed: z.boolean(),
  rotting: z.boolean(),
  next_activity: SalesNextActivity.nullable(),
  currency: SalesCurrency,
  expected_value_minor: SalesMinorMoneyNonNegative.nullable().optional(),
  weighted_value_minor: SalesMinorMoneyNonNegative.nullable().optional(),
});

/** The tool's answer: the customer's open deals in board order. */
export const CustomerPipelineToolResult = z.object({
  customer_id: Uuid,
  deals: z.array(CustomerPipelineToolDeal),
});

export type OpportunityJson = z.infer<typeof Opportunity>;
export type OpportunityDetailJson = z.infer<typeof OpportunityDetail>;
export type PipelineStageJson = z.infer<typeof PipelineStage>;
export type OpportunitiesOrderToken = z.infer<typeof OpportunitiesOrder>;
